import { useMemo } from "react"
import { NoteFragment, PathItemType } from "@pathwright/graphql-client/src/generated"
import { sortFlatPathItems, unnestPathItems } from "../path/utils"
import { PathItem } from "./types"

export type GroupedNotes = {
  notes: NoteFragment[]
  total_notes: number
  item: PathItem
}
export type GroupedNotesByTarget = Record<number, GroupedNotes>

function useGroupedNotesByTarget({
  notes,
  pathItems
}: {
  notes: NoteFragment[]
  pathItems: PathItem[]
}): GroupedNotesByTarget {
  return useMemo(() => {
    const groupedNotesByTarget: GroupedNotesByTarget = {}
    // We unnest and sort the path items to ensure the list is ordered in the
    // manner we expect (flattened tree).
    const flatPathItems = sortFlatPathItems(unnestPathItems(pathItems))

    // We will whittle this list down as we assign notes to items.
    let unusedNotes = notes.slice()

    for (let item of Array.from(flatPathItems)) {
      const target: GroupedNotes = {
        notes: [],
        total_notes: 0,
        item
      }

      groupedNotesByTarget[item.source_id] = target

      var nextUnusedNotes: NoteFragment[] = []
      // Only retaining notes that have not been added to
      // an item's notes list. Cut down on looping.
      for (let note of unusedNotes) {
        if (note.step_id === item.id || note.step_id === item.source_id) {
          target.notes.push(note)
        } else {
          nextUnusedNotes.push(note)
        }
      }

      // Reassign unusedNotes for the next iteration.
      unusedNotes = nextUnusedNotes

      // Track child note count. Note: the parent items
      // come before their children (ensured by sortFlatPathItems() above).
      if (item.type === PathItemType.Lesson) {
        target.total_notes += target.notes.length
      } else if (
        item.type === PathItemType.Step ||
        item.type === PathItemType.Resource
      ) {
        target.total_notes = target.notes.length
        if (target.item.parent_source_id)
          groupedNotesByTarget[target.item.parent_source_id].total_notes +=
            target.total_notes
      }
    }

    return groupedNotesByTarget
  }, [notes, pathItems])
}

export default useGroupedNotesByTarget
